import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, FileQuestion } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getRouteErrorDetails } from "@/lib/route-error";

interface ErrorPageContentProps {
  error: unknown;
  pathname?: string;
  onRetry?: () => void;
}

export const ErrorPageContent = ({
  error,
  pathname,
  onRetry,
}: ErrorPageContentProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const resolvedPathname = pathname ?? location.pathname;

  const { isNotFound, title, message, code } = useMemo(
    () => getRouteErrorDetails(error, resolvedPathname),
    [error, resolvedPathname],
  );

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 text-center">
      <div className="w-full max-w-md space-y-6 rounded-xl border border-border bg-card p-8 shadow-none">
        <div className="flex justify-center">
          <div
            className={`flex h-16 w-16 items-center justify-center rounded-full ${
              isNotFound ? "signal-card-warning" : "signal-card-negative"
            }`}
          >
            {isNotFound ? (
              <FileQuestion className="h-8 w-8 signal-text-warning" />
            ) : (
              <AlertTriangle className="h-8 w-8 signal-text-negative" />
            )}
          </div>
        </div>

        <div className="space-y-2">
          <h1 className="text-4xl font-bold text-foreground">{code}</h1>
          <h2 className="text-xl font-semibold text-foreground/80">{title}</h2>
          <p className="break-words text-muted-foreground">{message}</p>
        </div>

        <div className="flex flex-col gap-2 pt-4">
          {onRetry ? (
            <Button onClick={onRetry} className="w-full cursor-pointer">
              Try again
            </Button>
          ) : null}
          <Button
            variant={onRetry ? "outline" : "default"}
            onClick={handleGoBack}
            className="w-full cursor-pointer"
          >
            Go back
          </Button>
        </div>
      </div>
    </div>
  );
};
